import React, { useState, useEffect } from 'react'
import { Form, FormGroup, Label, Input } from 'reactstrap'
import { getStudentIdData, addData, updateData } from '../../lib/studentadditionaolinfo'
import { TextEditor } from '../Common/TextEditor'
import { ToastNotification } from '../Lessons/CourseReport/CourseReportComponents'
import { useAuth } from '../../_helper/Auth/AuthProvider'

const AdditionalInfo = ({ studentId }) => {
	const [instructorNote, setInstructorNote] = useState('')
	const [studentNote, setStudentNote] = useState('')
	const [detail, setDetail] = useState('')
	const [recId, setRecId] = useState(0)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const [isDarkMode, setIsDarkMode] = useState(false)
	const [toastMessage, setToastMessage] = useState('')
	const [showToast, setShowToast] = useState(false)

	const showToastMessage = (message) => {
		setToastMessage(message)
		setShowToast(true)
		setTimeout(() => setShowToast(false), 3000)
	}

	const { user } = useAuth()
	const effectiveStudentId = studentId || user?.id

	const getData = async () => {
		try {
			setLoading(true)
			setError('')
			const data = await getStudentIdData(effectiveStudentId)
			if (data.length > 0) {
				setRecId(data[0].id)
				setStudentNote(data[0].studentNote || '')
				setInstructorNote(data[0].instructorNote || '')
				setDetail(data[0].detail || '')
			}
		} catch (error) {
			console.error('Error fetching additional info:', error)
			setError('Failed to load additional info')
		} finally {
			setLoading(false)
		}
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setLoading(true)

		try {
			if (recId > 0) {
				await updateData({
					id: recId,
					studentId: effectiveStudentId,
					studentNote: studentNote,
					instructorNote: instructorNote,
					detail: detail
				})
			} else {
				await addData({
					studentId: effectiveStudentId,
					studentNote: studentNote,
					instructorNote: instructorNote,
					detail: detail
				})
			}

			showToastMessage('Update successful!')
			window.scrollTo(0, 0)
			await getData() // Refresh data
		} catch (error) {
			console.error('Error saving additional info:', error)
			setError('Failed to save additional info')
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		if (effectiveStudentId) {
			getData()
		}
	}, [effectiveStudentId])

	useEffect(() => {
		const checkDarkMode = () => {
			setIsDarkMode(document.body.classList.contains('dark-only'))
		}
		checkDarkMode()
		const observer = new MutationObserver(checkDarkMode)
		observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
		return () => observer.disconnect()
	}, [])

	useEffect(() => {
		if (isDarkMode) {
			const style = document.createElement('style')
			style.id = 'additional-info-ckeditor-dark'
			style.textContent = `
				#editor .ck-editor__editable,
				#editor .ck-content,
				#editor .ck-content p,
				#editor .ck-content h1,
				#editor .ck-content h2,
				#editor .ck-content h3,
				#editor .ck-content h4,
				#editor .ck-content h5,
				#editor .ck-content h6,
				#editor .ck-content li,
				#editor .ck-content span,
				#editor .ck-content div,
				#editor .ck-content strong,
				#editor .ck-content em,
				#editor .ck-content ul,
				#editor .ck-content ol {
					color: #000 !important;
				}
			`
			document.head.appendChild(style)
			return () => {
				const existingStyle = document.getElementById('additional-info-ckeditor-dark')
				if (existingStyle) {
					existingStyle.remove()
				}
			}
		} else {
			const existingStyle = document.getElementById('additional-info-ckeditor-dark')
			if (existingStyle) {
				existingStyle.remove()
			}
		}
	}, [isDarkMode])


	return (
		<div className="card mb-4 shadow-sm" style={{ borderRadius: '8px' }}>
			<ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
			<div className="card-header border-bottom d-flex justify-content-between align-items-center">
				<div className="d-flex align-items-center">
					<button
						type="button"
						className="btn btn-link p-0 me-2"
						data-bs-toggle="collapse"
						data-bs-target="#collapseAdditionalInfo"
						aria-expanded="false"
						aria-controls="collapseAdditionalInfo"
					>
						<i className="fa fa-chevron-down"></i>
					</button>
					<h5 className="mb-0 d-inline-block">Additional Info</h5>
				</div>
			</div>
			<div className="collapse" id="collapseAdditionalInfo">
				<div className="card-body">
					{error && (
						<div className="alert alert-danger alert-dismissible fade show" role="alert">
							<strong>Error:</strong> {error}
							<button type="button" className="btn-close" onClick={() => setError('')} aria-label="Close"></button>
						</div>
					)}

					{loading ? (
						<div className="text-center">
							<div className="spinner-border" role="status">
								<span className="visually-hidden">Loading...</span>
							</div>
							<p className="mt-2">Loading additional info...</p>
						</div>
					) : (
						<Form onSubmit={handleSubmit}>
							<FormGroup className="mb-3">
								<Label className="form-control-label fw-bold" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>
									Do you wish to provide details of circumstances or qualifications not reflected in the application?
								</Label>
							</FormGroup>

							<hr />

							<FormGroup className="mb-3">
								<Label htmlFor="counselorsNote" className="form-control-label fw-bold mb-2" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>
									Counselor's Note:
								</Label>
								<Input
									type="textarea"
									id="counselorsNote"
									rows="3"
									value={instructorNote}
									onChange={(e) => setInstructorNote(e.target.value)}
									className="form-control border-radius-10"
									placeholder="Enter counselor's notes"
									style={{
										backgroundColor: isDarkMode ? '#2a2d35' : '#cfe2f3',
										color: isDarkMode ? '#fff' : 'inherit',
										resize: 'vertical',
										minHeight: '80px',
										border: '1px solid #e9ecef',
										boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
									}}
								/>
							</FormGroup>

							<FormGroup className="mb-3">
								<Label htmlFor="studentsNote" className="form-control-label fw-bold mb-2" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>
									Students Note:
								</Label>
								<Input
									type="textarea"
									id="studentsNote"
									rows="3"
									value={studentNote}
									onChange={(e) => setStudentNote(e.target.value)}
									className="form-control border-radius-10"
									placeholder="Enter student's notes"
									style={{
										backgroundColor: isDarkMode ? '#2a2d35' : '#cfe2f3',
										color: isDarkMode ? '#fff' : 'inherit',
										resize: 'vertical',
										minHeight: '80px',
										border: '1px solid #e9ecef',
										boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
									}}
								/>
							</FormGroup>

							<FormGroup className="mb-3">
								<Label htmlFor="details" className="form-control-label fw-bold mb-2" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>
									Details:
								</Label>
								<div id="editor">
									<TextEditor value={detail} setValue={setDetail} />
								</div>
							</FormGroup>

							<hr />

							<div className="d-flex justify-content-end">
								<button type="submit" className="btn btn-primary" disabled={loading}>
									<i className="fa fa-save me-2"></i>
									{loading ? 'Updating...' : 'Update Additional Info'}
								</button>
							</div>
						</Form>
					)}
				</div>
			</div>
		</div>
	)
}

export { AdditionalInfo }
